/** Calendar reporting quarter for the Network dashboard (Q1–Q4). */

export interface NetworkReportingPeriod {
  quarter: number;
  year: number;
}

export const NETWORK_REPORTING_EARLIEST_YEAR = 2023;

const QUARTER_END_MONTH = [3, 6, 9, 12] as const;
const RELEASE_SUFFIX_MONTH = ["Mar", "Jun", "Sep", "Dec"] as const;

function stripTime(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Last calendar day of a reporting quarter (local time). */
export function reportingPeriodEndDate(quarter: number, year: number): Date {
  const q = Math.min(4, Math.max(1, quarter));
  const endMonth = QUARTER_END_MONTH[q - 1];
  return new Date(year, endMonth, 0);
}

/** ISO date (YYYY-MM-DD) for the end of the reporting quarter. */
export function reportingPeriodEndDateIso(quarter: number, year: number): string {
  const end = reportingPeriodEndDate(quarter, year);
  const y = end.getFullYear();
  const m = String(end.getMonth() + 1).padStart(2, "0");
  const d = String(end.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** CMS open-data release suffix for the quarter (last month of quarter). */
export function reportingPeriodReleaseSuffix(quarter: number, year: number): string {
  const q = Math.min(4, Math.max(1, quarter));
  return `${RELEASE_SUFFIX_MONTH[q - 1]}${year}`;
}

export function formatReportingPeriodLabel(period: NetworkReportingPeriod): string {
  return `Q${period.quarter} ${period.year}`;
}

export function shiftReportingPeriod(
  period: NetworkReportingPeriod,
  offset: number,
): NetworkReportingPeriod {
  let q = period.quarter + offset;
  let y = period.year;
  while (q > 4) {
    q -= 4;
    y += 1;
  }
  while (q < 1) {
    q += 4;
    y -= 1;
  }
  return { quarter: q, year: y };
}

/**
 * Previous CMS reporting period to probe when a network has no snapshot data.
 * Product fallback sequence skips Q4 when crossing into a prior year.
 */
export function getPreviousNetworkDataPeriod(
  period: NetworkReportingPeriod,
  earliestYear = NETWORK_REPORTING_EARLIEST_YEAR,
): NetworkReportingPeriod | null {
  if (period.quarter > 1) {
    return { quarter: period.quarter - 1, year: period.year };
  }
  if (period.year <= earliestYear) {
    return null;
  }
  return { quarter: 3, year: period.year - 1 };
}

/**
 * Calendar quarter containing `refDate` (Q1–Q4), regardless of whether the quarter has ended.
 */
export function getCurrentCalendarQuarter(refDate: Date = new Date()): NetworkReportingPeriod {
  const today = stripTime(refDate);
  const year = today.getFullYear();
  const quarter = Math.ceil((today.getMonth() + 1) / 3);
  return { quarter, year };
}

/**
 * Most recently completed calendar quarter relative to `refDate`.
 * On the last day of a quarter, that quarter counts as completed.
 */
export function getCurrentCompletedQuarter(refDate: Date = new Date()): NetworkReportingPeriod {
  const today = stripTime(refDate);
  const year = today.getFullYear();
  const currentQuarter = Math.ceil((today.getMonth() + 1) / 3);
  const end = reportingPeriodEndDate(currentQuarter, year);
  if (today >= end) {
    return { quarter: currentQuarter, year };
  }
  return shiftReportingPeriod({ quarter: currentQuarter, year }, -1);
}

/**
 * Quarters selectable for a given year.
 * Past years: all four quarters. Current year: Q1 through the active calendar quarter.
 * Future years: none.
 */
export function getSelectableQuarters(
  year: number,
  refDate: Date = new Date(),
): number[] {
  const { quarter: currentQuarter, year: currentYear } = getCurrentCalendarQuarter(refDate);
  return [1, 2, 3, 4].filter((q) => {
    if (year < currentYear) return true;
    if (year > currentYear) return false;
    return q <= currentQuarter;
  });
}

/** Year options for the reporting-period picker (oldest CMS data through current year). */
export function getReportingYearOptions(
  refDate: Date = new Date(),
  earliestYear = NETWORK_REPORTING_EARLIEST_YEAR,
): number[] {
  const currentYear = refDate.getFullYear();
  const years: number[] = [];
  for (let y = currentYear; y >= earliestYear; y -= 1) {
    years.push(y);
  }
  return years;
}

export function reportingPeriodToApiParams(
  period: NetworkReportingPeriod,
): Record<string, string> {
  return {
    reporting_quarter: String(period.quarter),
    calendar_year: String(period.year),
    as_of_date: reportingPeriodEndDateIso(period.quarter, period.year),
    release_suffix: reportingPeriodReleaseSuffix(period.quarter, period.year),
  };
}

/** Clamp a period to the latest completed quarter when year/quarter is out of range. */
export function normalizeReportingPeriod(
  period: NetworkReportingPeriod,
  refDate: Date = new Date(),
): NetworkReportingPeriod {
  const selectable = getSelectableQuarters(period.year, refDate);
  if (selectable.length === 0) {
    return getCurrentCompletedQuarter(refDate);
  }
  if (selectable.includes(period.quarter)) {
    return period;
  }
  return { quarter: selectable[selectable.length - 1], year: period.year };
}
